import React, { FC, useEffect } from "react";
import axios from "axios";
import { RootStateOrAny, useDispatch, useSelector } from "react-redux";
import { Route, Switch, useHistory } from "react-router";
import { Modal } from "antd";
import { saveAs } from "file-saver";
import DocumentTemplateList from "../../components/DocumentTemplates/DocumentTemplateList";
import CreateDocumentTemplate from "../../components/DocumentTemplates/CreateDocumentTemplate";
import { DictionaryDto } from "../../lib/interfaces/DictionaryDto";
import {
  CloseDocumentTemplate,
  CreateTemplate,
  DownloadDocumentTemplateFile,
  UpdateDocumentTemplate,
  UpdatedPropsDocumentTemplate,
} from "../../lib/interfaces/DocumentTemplate";
import LocalStorageService from "../../services/LocalStorageService";
import { fetchReportType } from "../../store/slices/dicts";
import {
  cleanTemplateVersions,
  fetchDocumentTemplates,
  fetchDocumentTemplateVersions,
  IDocumentTemplates,
} from "../../store/slices/documentTemplates";
import {
  createDocumentTemplateRoute,
  deleteDocumentTemplateRoute,
  downloadDocumentTemplateFileRoute,
  updateDocumentTemplateRoute,
} from "../../lib/routes/documentTemplates";
import { hasAccess } from "../../components/RolesModule/hasAccess";
import { MENU_ADMIN_REPORT_TEMPLATE } from "../../lib/const/permissions";
import { toHttpFormat } from "../../lib/utils/formatDate";

const acceptedFileTypes = [
  ".xls",
  ".xlsx",
  "vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "vnd.ms-excel",
];

const DocumentTemplatesContainer = () => {
  
  const history = useHistory();
  const dispatch = useDispatch();

  const { templates }: IDocumentTemplates = useSelector(
    (state: RootStateOrAny) => state.documentTemplates
  );

  const searchTemplates = ({
    onDate,
    reportTypeId,
  }: {
    onDate?: string;
    reportTypeId?: number;
  }) => {
    dispatch(fetchDocumentTemplates({ onDate, reportTypeId }));
  };

  useEffect(() => {
    dispatch(fetchReportType());
    searchTemplates({});
  }, []);

  const reportType: DictionaryDto[] = useSelector(
    (state: RootStateOrAny) => state.dicts.reportType
  );

  const createTemplate: CreateTemplate = (createdTemplate) => {
    const newTemplate = {
      ...createdTemplate,
      tenantId: LocalStorageService.getTenantId(),
      reportTemplate: undefined,
    };

    const formData = new FormData();

    if (createdTemplate.reportTemplate) {
      formData.append("file", createdTemplate.reportTemplate);
    }

    formData.append("templateDsc", JSON.stringify(newTemplate));

    axios
      .post(createDocumentTemplateRoute(), formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then(() => {
        searchTemplates({});
        history.push("/document-templates");
      })
      .catch((e) => {
        Modal.error({
          title: "Ошибка создания шаблона",
          content: e?.message,
        });
      });
  };

  const getTemplateVersions = (reportId: number) => {
    dispatch(fetchDocumentTemplateVersions(reportId));
  };

  const resetTemplateVersions = () => {
    dispatch(cleanTemplateVersions());
  };

  // Сброс версий при поиске шаблонов
  useEffect(() => {
    resetTemplateVersions();
  }, [templates]);

  const downloadDocumentTemplateFile: DownloadDocumentTemplateFile = ({ reportId, onDate }) => {
    axios
      .get(downloadDocumentTemplateFileRoute({ reportId, onDate }), {
        responseType: "blob",
      })
      .then(({ data }) => {
        saveAs(data, `template_${reportId}.xlsx`);
      })
      .catch(() => {
        Modal.error({
          title: "Ошибка загрузки файла шаблона",
        });
      });
  };

  const updateDocumentTemplate: UpdateDocumentTemplate = ({
    reportId,
    fromDate,
    force,
    updatedTemplate,
  }) => {
    const copyTemplate: Partial<UpdatedPropsDocumentTemplate["updatedTemplate"]> = {
      ...updatedTemplate,
      reportTemplate: undefined,
    };

    const formData = new FormData();

    if (updatedTemplate.reportTemplate) {
      formData.append("file", updatedTemplate.reportTemplate);
    }

    formData.append("templateDsc", JSON.stringify(copyTemplate));

    return axios
      .patch(updateDocumentTemplateRoute({ reportId, fromDate, force }), formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((respose) => {
        getTemplateVersions(reportId);
        Modal.success({
          content: force ? "Шаблон изменен" : "Версия создана",
        });
        return Promise.resolve(respose);
      })
      .catch((e) => {
        Modal.error({
          title: force ? "Ошибка изменения шаблона" : "Ошибка создания версии",
          content: e?.message,
        });
        return Promise.reject();
      });
  };

  const closeDocumentTemplate: CloseDocumentTemplate = ({ reportId, fromDate, onDate }) =>
    axios
      .delete(deleteDocumentTemplateRoute({ reportId, fromDate }))
      .then((respose) => {
        searchTemplates(onDate ? { onDate: toHttpFormat(onDate) } : {});
        // getTemplateVersions(reportId);
        Modal.success({
          content: "Дата закрытия версии изменена",
        });
        return Promise.resolve(respose);
      })
      .catch((e) => {
        Modal.error({
          title: "Ошибка изменения даты закрытия",
          content: e?.message,
        });
        return Promise.reject();
      });

  return (
    <div className="container_height">
      <h1 style={{ padding: "7px 0 0 12px" }}>Шаблоны документов</h1>
      <Switch>
        <Route path="/document-templates" exact>
          <DocumentTemplateList
            reportType={reportType}
            acceptedFileTypes={acceptedFileTypes}
            searchTemplates={searchTemplates}
            getTemplateVersions={getTemplateVersions}
            resetTemplateVersions={resetTemplateVersions}
            downloadDocumentTemplateFile={downloadDocumentTemplateFile}
            updateDocumentTemplate={updateDocumentTemplate}
            closeDocumentTemplate={closeDocumentTemplate}
          />
        </Route>
        <Route path="/document-templates/upload">
          {hasAccess(MENU_ADMIN_REPORT_TEMPLATE) && (
            <CreateDocumentTemplate
              reportType={reportType}
              acceptedFileTypes={acceptedFileTypes}
              createTemplate={createTemplate}
            />
          )}
        </Route>
      </Switch>
    </div>
  );
};

export default DocumentTemplatesContainer;
